import { ImageScaleView, View, ViewOpts } from '@play-co/timestep-core/ui';
import { DOWNSCALE_FACTOR, downscale } from 'src/conf/scale';
import { ProgressBar } from 'src/game/components/shared/ProgressBar';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import sounds from 'src/lib/sounds';
import type { Match2Reward } from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import {
  CardsRewardTooltip,
  type CardsRewardTooltipPosition,
} from './CardsRewardTooltip';

type CardsProgressBarOpts = ViewOpts & {
  title?: string;
  leftIcon?: string;
  rightIcon?: string;
  rewards?: Match2Reward[];
  rewardsTooltipPosition?: CardsRewardTooltipPosition;
};

const ICON_SIZE = 70;
const ICON_OVERFLOW = 45;

export class CardsProgressBar extends View {
  private readonly barContainer: View;
  private readonly barWidth: number;
  private readonly barLeft: number;
  private titleTab: ImageScaleView;
  private title: LangBitmapFontTextView;
  private count: LangBitmapFontTextView;
  private progressBar: ProgressBar;
  private leftIcon?: ImageScaleView;
  private rightIcon?: ImageScaleView;
  private rewards: Match2Reward[] = [];
  private rewardsTooltip?: CardsRewardTooltip;
  private readonly rewardsTooltipPosition: CardsRewardTooltipPosition;

  constructor(opts: CardsProgressBarOpts) {
    const barWidth = opts.width ?? downscale(275);
    const barHeight = opts.height ?? downscale(71);
    const hasIcons = !!(opts.leftIcon || opts.rightIcon);
    const iconOverflow = hasIcons ? ICON_OVERFLOW : 0;
    const width = barWidth + iconOverflow * 2;
    const height = hasIcons ? Math.max(barHeight, ICON_SIZE) : barHeight;

    super({
      ...opts,
      width,
      height,
    });

    this.barWidth = barWidth;
    this.barLeft = iconOverflow;
    this.barContainer = new View({
      superview: this,
      width: barWidth,
      height: barHeight,
      x: this.style.width * 0.5,
      y: this.style.height * 0.5,
      centerOnOrigin: true,
      canHandleEvents: false,
    });

    this.titleTab = new ImageScaleView({
      superview: this.barContainer,
      image: 'assets/ui/cards/common/hor_progress_tab.png',
      width: downscale(369),
      height: downscale(62),
      x: barWidth * 0.5,
      y: -20,
      centerOnOrigin: true,
    });

    this.title = new LangBitmapFontTextView({
      superview: this.barContainer,
      width: this.titleTab.style.width - 60,
      height: this.titleTab.style.height - 20,
      x: this.titleTab.style.x,
      y: this.titleTab.style.y - 2,
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      size: 26,
      font: 'Body',
      color: '#6F5714',
    });

    this.progressBar = new ProgressBar({
      superview: this.barContainer,
      bgStyle: {
        image: 'assets/ui/cards/common/hor_progress_base.png',
        width: barWidth,
        height: barHeight,
        scaleMethod: '3slice',
        sourceSlices: {
          horizontal: { left: 60, right: 60 },
          vertical: { top: 0, bottom: 0 },
        },
        imgScale: DOWNSCALE_FACTOR,
      },
      fillStyle: {
        image: 'assets/ui/cards/common/hor_progress_fill.png',
        width: barWidth - 6,
        height: barHeight - 9,
        x: 4,
        y: 4,
        paddingRight: 8,
        scaleMethod: '3slice',
        sourceSlices: {
          horizontal: { left: 30, right: 30 },
          vertical: { top: 0, bottom: 0 },
        },
        imgScale: DOWNSCALE_FACTOR,
      },
      minWidth: 40,
      domain: [0, 1],
      progress: 0,
    });

    this.progressBar.updateOpts({
      x: barWidth * 0.5,
      y: barHeight * 0.5,
      centerOnOrigin: true,
    });

    this.progressBar.progressFill.updateOpts({
      opacity: 0,
    });

    this.count = new LangBitmapFontTextView({
      superview: this.barContainer,
      width: barWidth,
      height: barHeight,
      align: 'center',
      verticalAlign: 'center',
      size: 26,
      font: 'Title',
    });

    this.setTitle(opts.title);
    this.setLeftIcon(opts.leftIcon);
    this.rewardsTooltipPosition = opts.rewardsTooltipPosition ?? 'above';
    this.setRightIcon(opts.rightIcon);
    this.setRewards(opts.rewards);
  }

  public setProgress(current: number, total: number) {
    const value = current / total;
    this.progressBar.progress = value;
    this.updateFillVisibility(value);
    this.count.localeText = () => `${current}/${total}`;
  }

  private updateFillVisibility(value: number) {
    this.progressBar.progressFill.updateOpts({
      opacity: value > 0 ? 1 : 0,
    });
  }

  public setTitle(title?: string) {
    if (!title) {
      this.titleTab.hide();
      this.title.hide();
      return;
    }
    this.titleTab.show();
    this.title.show();
    this.title.localeText = () => title;
  }

  public setLeftIcon(image?: string) {
    if (!image) {
      this.leftIcon?.hide();
      return;
    }

    if (!this.leftIcon) {
      this.leftIcon = new ImageScaleView({
        superview: this,
      });
    }

    this.leftIcon.updateOpts({
      image,
      width: ICON_SIZE,
      height: ICON_SIZE,
      x: this.barLeft - 10,
      y: this.style.height * 0.5,
      centerOnOrigin: true,
      visible: true,
      scaleMethod: 'contain',
    });
  }

  public setRightIcon(image?: string) {
    if (!image) {
      this.rightIcon?.hide();
      this.rewardsTooltip?.hideTooltip();
      return;
    }

    if (!this.rightIcon) {
      this.rewardsTooltip = new CardsRewardTooltip({
        superview: this,
        x: this.barLeft + this.barWidth + 10,
        y: this.rewardsTooltipPosition === 'above' ? 0 : this.style.height,
        position: this.rewardsTooltipPosition,
        zIndex: 10,
      });
      this.rightIcon = new ImageScaleView({
        superview: this,
        canHandleEvents: true,
      });
      this.rightIcon.onInputStart = () => this.showRewardsTooltip();
    }

    this.rightIcon.updateOpts({
      image,
      width: ICON_SIZE,
      height: ICON_SIZE,
      x: this.barLeft + this.barWidth + 10,
      y: this.style.height * 0.5,
      centerOnOrigin: true,
      visible: true,
      scaleMethod: 'contain',
    });
  }

  /**
   * Sets the rewards displayed when the right-hand chest is tapped.
   */
  public setRewards(rewards?: Match2Reward[]) {
    this.rewards = rewards ? [...rewards] : [];
    this.rightIcon?.updateOpts({
      canHandleEvents: this.rewards.length > 0,
    });

    if (this.rewards.length === 0) {
      this.rewardsTooltip?.hideTooltip();
    }
  }

  private showRewardsTooltip() {
    if (!this.rightIcon?.style.visible || this.rewards.length === 0) return;
    if (!this.rewardsTooltip) return;

    sounds.sfx.play('move');
    void this.rewardsTooltip.showAnimated(this.rewards);
  }
}
